import { Injectable } from '@angular/core';
import { DeadEnd, StationData, StationTags, TrackCrossing, TrackTagAsset, TrapPoint, UtilityAsset } from '../models/model';

@Injectable({
  providedIn: 'root'
})
export class TaglocationService {


  private stationData!: StationData;
  constructor() { }

  // sets the current station data.
  setStationData(data: StationData) {
    this.stationData = data;
  }

  // the station data the location lookups currently resolve against, so a caller that
  // points the service at another layout can put the previous one back.
  getStationData(): StationData {
    return this.stationData;
  }


 // To get the tags presert in  a track.
  private getTrackTags(tags: StationTags): TrackTagAsset[] {
    const allTags: any = tags;
    return Object.keys(allTags).flatMap((key) =>
      allTags[key].flatMap((tag: any) => tag.assets ?? (tag.rfid_tag ? [tag] : []))
    ).filter(Boolean);
  }



  // calculate the actual(display) location of the moved(new) tag based on the offsets and actual location of the two closest tags
  getTagActualLocation(trackName: string, tags: any, newtagOffsetLocation: number, isRightToLeft:boolean): number {
    
    const closestTags = this.getClosestTagslocations(trackName, tags, newtagOffsetLocation, isRightToLeft);
    // closest tags min and max;
    const minTag = { ...closestTags.minTag };
    const maxTag = { ...closestTags.maxTag };
    
    const mainLines = this.stationData?.main_lines_direction_map?.main_lines;
    const directionFactor = isRightToLeft ? -1 : 1;
    if (minTag.id.includes('track-min') && mainLines?.includes(minTag.id.replace('track-min-', ''))) {
      minTag.tagActualLocation -= 0.300 * directionFactor;
    } else if (maxTag.id.includes('track-max') && mainLines?.includes(maxTag.id.replace('track-max-', ''))) {
      maxTag.tagActualLocation += 0.300 * directionFactor;
    }

    // calculate the actual location using ratio of offset location
    const ratio = (newtagOffsetLocation - minTag.tagOffset) / (maxTag.tagOffset - minTag.tagOffset);
    const actualLocation = minTag.tagActualLocation + (ratio * (maxTag.tagActualLocation - minTag.tagActualLocation));
    return parseFloat(actualLocation.toFixed(3));
  }

  // gets the two closest tags from the current offset location
  private getClosestTagslocations(trackName: string, stationTags: StationTags, newTagOffsetLocation: number, isRightToLeft:boolean): { minTag: {id: string,tagOffset: number, tagActualLocation: number }, maxTag: { id: string,tagOffset: number, tagActualLocation: number } } {
    // all tags
    const trackTags = this.getTrackTags(stationTags);
    // find the closest tag's offset and actual location or default location which is lesser(<) than current location
    let closestTagMinLocations = structuredClone(this.getClosestTagLocationMinLocations(trackName, trackTags, newTagOffsetLocation,isRightToLeft))
    // find the closest tag's offset and actual location or default location which is greater(>) than current location
    let closestTagMaxLocations = structuredClone(this.getClosestTagLocationMaxLocations(trackName, trackTags, newTagOffsetLocation,isRightToLeft));
    let leasDiffOffset=Infinity
    let tagoffsetDifference: any[] = []
   
    //Comparing offsets and appending
    for (const currentMin of closestTagMinLocations) {
      for (const currentMax of closestTagMaxLocations) {
        const currentDiff = Math.abs(currentMax.tagOffset - currentMin.tagOffset);
        if(currentDiff<leasDiffOffset){
          tagoffsetDifference=[{ minTag: currentMin, maxTag: currentMax }]
          leasDiffOffset=currentDiff
        }
        else if(currentDiff===leasDiffOffset){
          tagoffsetDifference.push({ minTag: currentMin, maxTag: currentMax })
        }
      }
    }
    
    if(tagoffsetDifference.length==1){
      return tagoffsetDifference[0]
    }
    else if(tagoffsetDifference.length>1){
      //Compare the actual locations
      let currentactualMinMaxTag = {
        minTag: structuredClone(tagoffsetDifference[0].minTag),
        maxTag: structuredClone(tagoffsetDifference[0].maxTag)
      };
      let tagActualLocation=Infinity
      const mintagsForActualLocation=structuredClone(tagoffsetDifference.map(item=>item.minTag))
      const maxtagsForActualLocation=structuredClone(tagoffsetDifference.map(item=>item.maxTag))
      for(const actualMin of mintagsForActualLocation){
        for(const actualMax of maxtagsForActualLocation){

          const actualDiff = Math.abs(actualMin.tagActualLocation - actualMax.tagActualLocation);

          if(actualDiff<tagActualLocation && actualDiff!==0){

            currentactualMinMaxTag.minTag=structuredClone(actualMin)
            currentactualMinMaxTag.maxTag=structuredClone(actualMax)
            tagActualLocation=actualDiff
          }
        }
      }
      return currentactualMinMaxTag

    }
    return { minTag: closestTagMinLocations[0], maxTag: closestTagMaxLocations[0] };
  }

  private getTrackReferencePoints(trackTags: TrackTagAsset[], isRightToLeft: boolean = false) {
    const tagPoints = trackTags.map(tag => ({
       id: tag.id,
      offset_location: Number(tag.offset_location),
      actual_location: Number(tag.location)
    }));
    const deadEndPoints = this.getDeadEndsOnTrack().map(de => ({
       id: de.id,
      offset_location: de.location,
      actual_location: de.display_location
    }));
    const trapPoints = this.getTrapPointsOnTrack().map(tp => ({
       id: tp.id ?? tp.asset_id,
      offset_location: tp.location,
      actual_location: tp.display_location
    }));
    const gatePoints = this.getGatesOnTrack().map(gate => ({
       id: gate.asset_id,
      offset_location: gate.offset_location,
      actual_location: gate.location
    }));
    const crossingPoints = this.getCrossingsOnTrack().flatMap(c => [
      {
        id: c.asset_id,
        offset_location: Number(c.Track_1_offset_Location),
        actual_location: Number(c['Track 1 Location'])
      },
      {
        id: c.asset_id,
        offset_location: Number(c.Track_2_offset_Location),
        actual_location: Number(c['Track 2 Location'])
      }
    ]);
    const offsetEntries: any[] = this.stationData?.track_start_end_locations ?? [];
    const actualEntries: any[] = this.stationData?.track_start_end_locations_without_offsets ?? [];
    const trackPoints = offsetEntries.flatMap((oe: any) => {
      const ae = actualEntries.find((a: any) => a.track_name === oe.track_name);
      if (!ae) return [];
      return [
        {
          id: `track-min-${oe.track_name}`,
          offset_location: this.getTrackOffsetLocation(oe.track_name, 'min_location', isRightToLeft),
          actual_location: this.getTrackActualLocation(oe.track_name, 'min_location', isRightToLeft)
        },
        {
          id: `track-max-${oe.track_name}`,
          offset_location: this.getTrackOffsetLocation(oe.track_name, 'max_location', isRightToLeft),
          actual_location: this.getTrackActualLocation(oe.track_name, 'max_location', isRightToLeft)
        },
      ];
    });
    return [...tagPoints, ...deadEndPoints, ...trapPoints, ...gatePoints, ...crossingPoints,...trackPoints];
  }

  // finds the actual and offset location of the tag which is closest to it but also lesser than the current value
 private getClosestTagLocationMinLocations(trackname: string, trackTags: TrackTagAsset[], tagOffsetLocation: number, isRightToLeft:boolean): { id: string, tagOffset: number, tagActualLocation: number }[] {
    const allPoints = (this.getTrackReferencePoints(trackTags)
      .filter(p => Number.isFinite(p.offset_location) && Number.isFinite(p.actual_location))) as { id?: string; offset_location: number; actual_location: number }[];
    const formattedAllPoints = allPoints.filter(tag =>
      isRightToLeft ? tag.offset_location > tagOffsetLocation : tag.offset_location < tagOffsetLocation
    );
    if (formattedAllPoints.length > 0) {
      // const closestLesserTag = formattedAllPoints.reduce((prev, curr) =>
      //   Math.abs(curr.offset_location - tagOffsetLocation) < Math.abs(prev.offset_location - tagOffsetLocation) ? curr : prev
      // );
      const closestLesserTag=Math.min(...formattedAllPoints.map(p => Math.abs(Number(p.offset_location) - tagOffsetLocation)))
      return allPoints.filter((p:any)=>Math.abs(Number(p.offset_location) - tagOffsetLocation)===closestLesserTag).map(p=>({id: p.id ?? `track-min-${trackname}`, tagOffset: p.offset_location, tagActualLocation: p.actual_location }));
    }

    return [{ id: `track-min-${trackname}`, tagOffset: this.getTrackOffsetLocation(trackname, "min_location", isRightToLeft), tagActualLocation: this.getTrackActualLocation(trackname, "min_location", isRightToLeft) }]
  }
 

  // finds the actual and offset location of the tag which is closest to it but also greater than the current value
   private getClosestTagLocationMaxLocations(trackName: string, trackTags: TrackTagAsset[], tagOffsetLocation: number, isRightToLeft:boolean): { id: string, tagOffset: number, tagActualLocation: number }[] {
    const allPoints = (this.getTrackReferencePoints(trackTags)
      .filter(p => Number.isFinite(p.offset_location) && Number.isFinite(p.actual_location))) as { id: string; offset_location: number; actual_location: number }[];
    const formattedAllPoints = allPoints.filter(tag =>
      isRightToLeft ? tag.offset_location < tagOffsetLocation : tag.offset_location > tagOffsetLocation
    );
    if (formattedAllPoints.length > 0) {
      const closestGreaterTag = Math.min(
        ...formattedAllPoints.map(p => Math.abs(Number(p.offset_location) - tagOffsetLocation))
      );
      return allPoints.filter((p:any)=>Math.abs(Number(p.offset_location) - tagOffsetLocation)===closestGreaterTag).map(p=>({id: p.id ?? `track-max-${trackName}`, tagOffset: p.offset_location, tagActualLocation: p.actual_location }));
    }
    return [{ id: `track-max-${trackName}`, tagOffset: this.getTrackOffsetLocation(trackName, "max_location", isRightToLeft), tagActualLocation: this.getTrackActualLocation(trackName, "max_location", isRightToLeft) }]
  }

//   // get the track min or max offset location 
//   private getTrackOffsetLocation(trackName: string, locationKey: "min_location" | "max_location"): number {
//     if (this.stationData && this.stationData.track_start_end_locations) {
//       const trackLocation = this.stationData.track_start_end_locations.find(track => track.track_name === trackName);
//       if (trackLocation && trackLocation[locationKey]) {
//         return Number(trackLocation[locationKey]) + (locationKey === "min_location" ? -0.200 : 0.200);
//       }
//     }
//     return 0;
//   }

//   // gets the track min or max actual locations
//   private getTrackActualLocation(trackName: string, locationKey: "min_location" | "max_location"): number {
//     if (this.stationData && this.stationData.track_start_end_locations_without_offsets) {
//       const trackLocation = this.stationData.track_start_end_locations_without_offsets.find(track => track.track_name === trackName);
//       if (trackLocation && trackLocation[locationKey]) {
//         return Number(trackLocation[locationKey] + (locationKey === "min_location" ? -0.300 : 0.300));
//       }
//     }
//     return 0;
//   }
// }

private getTrackOffsetLocation(trackName: string, locationKey: "min_location" | "max_location", isRightToLeft: boolean = false): number {
  if (this.stationData && this.stationData.track_start_end_locations) {
    const trackLocation = this.stationData.track_start_end_locations
      .find(track => track.track_name === trackName);
    if (trackLocation) {
      const key = isRightToLeft
        ? (locationKey === "min_location" ? "max_location" : "min_location")
        : locationKey;
      const rawValue = trackLocation[key];
      if (rawValue !== undefined && rawValue !== null) {
        let value = Number(rawValue);
        const isMainLine =
          this.stationData?.main_lines_direction_map?.main_lines?.includes(trackName);
        if (isMainLine) {
          value += (key === "min_location" ? -0.000 : 0.000);
        }
        return value;
      }
    }
  }
  return NaN;
}

private getTrackActualLocation(trackName: string, locationKey: "min_location" | "max_location", isRightToLeft: boolean = false): number {
  if (this.stationData && this.stationData.track_start_end_locations_without_offsets) {
    const trackLocation =
      this.stationData.track_start_end_locations_without_offsets
      .find(track => track.track_name === trackName);
    if (trackLocation) {
      const key = isRightToLeft
        ? (locationKey === "min_location" ? "max_location" : "min_location")
        : locationKey;
      const rawValue = trackLocation[key];
      if (rawValue !== undefined && rawValue !== null) {
        let value = Number(rawValue);
        const isMainLine =
          this.stationData?.main_lines_direction_map?.main_lines?.includes(trackName);
        if (isMainLine) {
          value += (key === "min_location" ? -0.300 : 0.300);
        }
        return value;
      }
    }
  }
  return NaN;
}


//#region  get offset location of the tag based on actual location
getTagOffsetLocation(
  trackName: string,
  tagActualLocation: number,
  isRightToLeft: boolean
): number | null {

  if (!this.stationData || !Number.isFinite(tagActualLocation)) {
    return null;
  }

  const trackTags = this.getTrackTags(this.stationData['Tags']);

  const { minPoint, maxPoint } : any =
    this.getClosestReferencePointsByActualLocation(
      trackName,
      trackTags,
      tagActualLocation,
      isRightToLeft
    );

  const actualSpan =
    maxPoint.actual_location - minPoint.actual_location;

  if (actualSpan === 0) {
    return Number(minPoint.offset_location.toFixed(3));
  }

  const ratio =
    (tagActualLocation - minPoint.actual_location) / actualSpan;

  const offset =
    minPoint.offset_location +
    ratio * (maxPoint.offset_location - minPoint.offset_location);

  return Number(offset.toFixed(3));
}

private getClosestReferencePointsByActualLocation(
  trackName: string,
  trackTags: TrackTagAsset[],
  actualLocation: number,
  isRightToLeft: boolean
) {
  const minPoints : any = this.getClosestReferencePointMinByActualLocation(
    trackName,
    trackTags,
    actualLocation,
    isRightToLeft
  );
  const maxPoints : any = this.getClosestReferencePointMaxByActualLocation(
    trackName,
    trackTags,
    actualLocation,
    isRightToLeft
  );
  
  
  let bestMin = minPoints[0];
  let bestMax = maxPoints[0];
  let bestDiff = Infinity;
  for (const min of minPoints) {
    for (const max of maxPoints) {
      const diff = Math.abs(min.offset_location - max.offset_location);
      if (diff < bestDiff && diff!==0) {
        bestDiff = diff;
        bestMin = min;
        bestMax = max;
      }
    }
  }

  return { minPoint: bestMin, maxPoint: bestMax };
}

private getClosestReferencePointMinByActualLocation(
  trackName: string,
  trackTags: TrackTagAsset[],
  actualLocation: number,
  isRightToLeft: boolean
) {

  const allPoints = this.getTrackReferencePoints(trackTags, isRightToLeft)
    .filter(p =>
      Number.isFinite(p.offset_location) &&
      Number.isFinite(p.actual_location)
    );

  const candidates = allPoints.filter((p : any) =>
    isRightToLeft
      ? p.actual_location > actualLocation
      : p.actual_location < actualLocation
  );

  if (candidates.length) {
    const minDistance = Math.min(...candidates.map((p : any) => Math.abs(p.actual_location - actualLocation)));
    return candidates.filter((p : any) => Math.abs(p.actual_location - actualLocation) === minDistance);
  }

  return [{
    id: `track-min-${trackName}`,
    offset_location: this.getTrackOffsetLocation(
      trackName,
      "min_location",
      isRightToLeft
    ),
    actual_location: this.getTrackActualLocation(
      trackName,
      "min_location",
      isRightToLeft
    )
  }];
}

private getClosestReferencePointMaxByActualLocation(
  trackName: string,
  trackTags: TrackTagAsset[],
  actualLocation: number,
  isRightToLeft: boolean
) {

  const allPoints = this.getTrackReferencePoints(trackTags, isRightToLeft)
    .filter(p =>
      Number.isFinite(p.offset_location) &&
      Number.isFinite(p.actual_location)
    );

  const candidates : any = allPoints.filter((p : any) =>
    isRightToLeft
      ? p.actual_location < actualLocation
      : p.actual_location > actualLocation
  );

  if (candidates.length) {
     const maxDistance = Math.min(...candidates.map((p : any) => Math.abs(p.actual_location - actualLocation)));
     return candidates.filter((p : any) => Math.abs(p.actual_location - actualLocation) === maxDistance);
  }

  return [{
    id: `track-max-${trackName}`,
    offset_location: this.getTrackOffsetLocation(
      trackName,
       "max_location",
      isRightToLeft
    ),
    actual_location: this.getTrackActualLocation(
      trackName,
      "max_location",
      isRightToLeft
    )
  }];
}
//#endregion



//#region Assets getters
getDeadEndsOnTrack(): DeadEnd[] {
    if (!this.stationData?.['DEAD END']) return [];
    return this.stationData['DEAD END'];
}

getTrapPointsOnTrack(): TrapPoint[] {
    if (!this.stationData?.['Trap Point']) return [];
    return this.stationData['Trap Point'];
}

getGatesOnTrack(): UtilityAsset[] {
  if (!this.stationData?.utils) return [];
  return this.stationData.utils.filter(gate => gate.type_of_asset === 'Gate');
}

getCrossingsOnTrack(): TrackCrossing[] {
  if (!this.stationData?.CROSSING) return [];
  return this.stationData.CROSSING;
}
//#endregion

}